/**
 * 물의 포화증기압 — 스크러버 등에서 가스 속 수분의 응축·증발을 볼 때 쓴다.
 *
 * 근거: Antoine 식 (NIST Chemistry WebBook, water, Stull 1947 데이터
 *   재수록 계수). 두 온도 구간으로 나뉜다.
 *
 *   log10(P[bar]) = A − B / (T[K] + C)
 *
 *   구간 1: 255.9 ~ 373 K   A=5.40221, B=1838.675, C=−31.737
 *   구간 2: 379 ~ 573 K     A=5.20389, B=1733.926, C=−39.485
 *
 * ⚠ 다성분 비이상 혼합물의 정식 기액평형(VLE)이 아니다 — **물 한 성분의
 *   순물질 포화증기압**이다. 습윤가스 중 수분의 이슬점·포화 계산처럼
 *   "물만 응축·증발한다"고 볼 수 있는 경우에만 쓴다. 다른 성분이 같이
 *   응축하는 경우(탄화수소 이슬점 등)는 다성분 VLE가 필요해 이 함수의
 *   범위 밖이다.
 *
 * 정확도: 100 ℃ 이하에서는 실험값과 1% 이내로 맞는다. 구간 2(379~573 K,
 *   약 106~300 ℃)는 온도가 높아질수록 오차가 커져 200 ℃ 부근에서 3% 안팎
 *   차이가 난다(Antoine 식 자체의 한계 — Stull 1947 원자료가 이 구간
 *   상한으로 갈수록 덜 정확하다). **스크린용 계산이며, 150 ℃를 넘는
 *   고온 스크러버/응축 설계의 최종 근거로는 쓰지 말 것** — 정식 스팀표나
 *   공정 시뮬레이터로 재확인해야 한다.
 *   범위를 완전히 벗어나면(255.9 K 미만 또는 573 K 초과) 경고와 함께
 *   가장 가까운 구간 식으로 외삽한다.
 */

interface AntoineRange {
  readonly minK: number;
  readonly maxK: number;
  readonly A: number;
  readonly B: number;
  readonly C: number;
}

const RANGES: readonly AntoineRange[] = [
  { minK: 255.9, maxK: 373, A: 5.40221, B: 1838.675, C: -31.737 },
  { minK: 379, maxK: 573, A: 5.20389, B: 1733.926, C: -39.485 },
];

export interface WaterSaturationPressureResult {
  /** 포화증기압 (kPa 절대) */
  readonly kpaAbs: number;
  /** 입력 온도가 두 구간 다 벗어나 외삽했으면 true */
  readonly extrapolated: boolean;
}

/** 온도(K)에서 물의 포화증기압을 Antoine 식으로 구한다. */
export function waterSaturationPressureKpa(
  temperatureK: number,
): WaterSaturationPressureResult {
  if (!Number.isFinite(temperatureK) || temperatureK <= 0) {
    return { kpaAbs: Number.NaN, extrapolated: true };
  }

  const inRange = RANGES.find(
    (r) => temperatureK >= r.minK && temperatureK <= r.maxK,
  );
  const range =
    inRange ??
    // 범위 밖이면 더 가까운 구간으로 외삽한다.
    (temperatureK < RANGES[0]!.minK ? RANGES[0]! : RANGES[RANGES.length - 1]!);

  const logPBar = range.A - range.B / (temperatureK + range.C);
  const pBar = Math.pow(10, logPBar);

  return { kpaAbs: pBar * 100, extrapolated: inRange === undefined };
}
